import React, { useState } from 'react';
import Card from "../components/Structure/Card.tsx";
import TextInput from '../components/Form/TextInput.tsx';
import Datepicker from '../components/Form/Datepicker.tsx';
import Button from '../components/Form/Button.tsx';
import Select from '../components/Form/Select.tsx';
import { Link } from 'react-router-dom';
import { useAuthlessApi } from '../features/auth/hooks/use-authless-api.ts';
import { useHelper } from "../features/functions/helper.tsx";
import { ApiResponseType } from '../features/api/types.ts';
import PasswordInputWithValidation from '../components/Form/PasswordInputWithValidation.tsx';

export default function AccountActivation() {
    const [isLoading, setIsLoading] = useState(false);
    const [contactNumber, setContactNumber] = useState('');
    const [date, setDate] = useState(null);
    const [repeatPassword, setRepeatPassword] = useState('');
    const [passwordCheckPassed, setPasswordCheckPassed] = useState<boolean>(false);
    const [email, setEmail] = useState('');
    const [emailType, setEmailType] = useState('private');

    const [contactNumberValid, setContactNumberValid] = useState(true);
    const [dateValid, setDateValid] = useState(true);
    const [responseMessage, setResponseMessage] = useState<any>();
    const [responseMessageClass, setResponseMessageClass] = useState('');

    const [currentStep, setCurrentStep] = useState(0);

    const { validateContactNumberWithDOB, setAccountPassword, setAccountEmail } = useAuthlessApi();

    const { formatDate } = useHelper();

    const handlePWFormChange = (formData, inputPasswordCheckPassed) => {
        setRepeatPassword(formData.repeatpassword);
        setPasswordCheckPassed(inputPasswordCheckPassed);
    }
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setResponseMessage('');
        switch (currentStep) {
            case 0:
        
                let errors = 0;
                if (contactNumber.length > 0) { setContactNumberValid(true); } else { setContactNumberValid(false); errors++; }
                if (date !== null) { setDateValid(true); } else { setDateValid(false); errors++; }

                if (errors > 0) {
                    setIsLoading(false);
                    return;
                }
                
                validateContactNumberWithDOB(contactNumber, formatDate(new Date(date), 'ddmmyyyy')).then(res => {
                    if (res.type === ApiResponseType.CLIENT_ERROR || res.type === ApiResponseType.SERVER_ERROR || res.type === ApiResponseType.FORM_INVALID) {
                        setResponseMessage(<span>{res.payload.error}</span>);
                        setResponseMessageClass('error');
                        setIsLoading(false);
                    } else if (res.type === ApiResponseType.OK) {
                        if (res.payload.email) {
                            setEmail(res.payload.email);
                        }
                        setResponseMessage(<span>Kontodaten erfolgreich validiert...</span>);
                        setResponseMessageClass('success');
                        setTimeout(() => {
                            setCurrentStep(1);
                            setResponseMessage('');
                            setResponseMessageClass('');
                            setPasswordCheckPassed(false);
                            setRepeatPassword('');
                            setIsLoading(false);
                        }, 2000);
                    }
                }).catch(err => {
                    setIsLoading(false);
                })
                
                break;
            case 1:
                
                setAccountPassword(contactNumber, formatDate(new Date(date), 'ddmmyyyy'), repeatPassword).then(res => {
                    if (res.type === ApiResponseType.CLIENT_ERROR || res.type === ApiResponseType.SERVER_ERROR || res.type === ApiResponseType.FORM_INVALID) {
                        setResponseMessage(<span>{res.payload.error}</span>);
                        setResponseMessageClass('error');
                        setIsLoading(false);
                    } else if (res.type === ApiResponseType.OK) {
                        setResponseMessage(<span>Passwort gesetzt...</span>);
                        setResponseMessageClass('success');
                        setTimeout(() => {
                            setCurrentStep(2);
                            setResponseMessage('');
                            setResponseMessageClass('');
                            setPasswordCheckPassed(false);
                            setRepeatPassword('');
                            setIsLoading(false);
                        }, 2000);
                    }
                }).catch(err => {
                    setIsLoading(false);
                });                

                break;

            case 2:

                setAccountEmail(contactNumber, formatDate(new Date(date), 'ddmmyyyy'), email, emailType).then(res => {
                    if (res.type === ApiResponseType.CLIENT_ERROR || res.type === ApiResponseType.SERVER_ERROR || res.type === ApiResponseType.FORM_INVALID) {
                        setResponseMessage(<span>res.payload.error</span>);
                        setResponseMessageClass('error');
                    } else if (res.type === ApiResponseType.OK) {
                        setResponseMessage(<><span>Eine E-Mail zur Kontoaktivierung wird demnächst verschickt. Bitte kontrollieren Sie auch Ihren Spam-Ordner.</span><br/><span>Bitte klicken Sie den Link in der E-Mail zur abschließenden Kontoaktivierung. Danach können Sie sich mit Ihrem Benutzernamen und Ihrem Passwort einloggen und die hinterlegten Daten prüfen, korrigieren und ergänzen.</span></>);
                        setResponseMessageClass('success');
                        setCurrentStep(3);
                    }
                    setIsLoading(false);
                }).catch(err => {
                    setIsLoading(false);
                });

                break;
        }
    }

    return (
        <Card
            p={'var(--spacing-2xl)'}
            gap={'var(--spacing-xl)'}
            className='wide'
        >
            <h1 className="h3 align-center">Konto aktivieren</h1>
            <form onSubmit={handleSubmit} className={`login-form${isLoading ? ' loading' : ''}`}>
                <div className="loader"></div>
                { currentStep === 0 && (
                    <>
                        <TextInput
                            onChange={(e:any) => setContactNumber(e.target.value)}
                            value={contactNumber}
                            label='Kontaktnummer'
                            name='name'
                            type='text'
                            ariaLabel='Kontaktnummer'
                            required
                        />
                        <Datepicker
                            selected={date}
                            onChange={(inputDate) => {
                                setDate(inputDate);
                            }}
                            locale='de'
                            dateFormat='dd.MM.yyyy'
                            showYearDropdown={true}
                            showMonthDropdown={true}
                            maxDate={new Date()}
                            label='Geburtsdatum'
                            isRequired={true}
                            name='date_of_birth'
                        />
                    </>
                ) }
                { currentStep === 1 && (
                    <PasswordInputWithValidation 
                        outerChangeHandler={handlePWFormChange}
                        additionalHintUpper={true}
                    />
                ) }
                { currentStep === 2 && (
                    <>
                        <div>
                            <p className='text-xs'>Bitte teilen Sie uns mit, über welche E-Mailadresse der FPSB Deutschland mit Ihnen kommunizieren soll.</p>
                            <p className='text-xs'>Geben Sie dabei auch an, ob es sich um eine private oder um eine geschäftliche Mailadresse handelt.</p>
                            <p className='text-xs'>An diese E-Mailadresse senden einem Link zur Verifikation und Aktivierung.</p>
                            <p className='text-xs'>Unter dem Menüpunkt „Profilinformationen“ können Sie Ihre Mailadressen und Kontaktdaten jederzeit ändern.</p>
                        </div>
                        <TextInput
                            onChange={(e:any) => setEmail(e.target.value)}
                            value={email}
                            label='E-Mail'
                            name='email'
                            type='email'
                            ariaLabel='E-Mail'
                            required
                            autocomplete='email'
                        />
                        <Select
                            isSearchable={false}
                            isClearable={false}
                            label='Art der Mail'
                            placeholder='Art auswählen'
                            name='contact_gender'
                            onChange={(event) => {setEmailType(event.value)}}
                            defaultValue={'private'}
                            options={[
                                {
                                    value: 'private',
                                    label: 'Privat'
                                },
                                {
                                    value: 'business',
                                    label: 'Geschäftlich'
                                }
                            ]}
                            isRequired={true}
                        />
                    </>
                ) }
                { responseMessage ? (
                    <div className={`bright message${responseMessageClass ? ' ' + responseMessageClass : ''}`} role="alert">
                        <div>
                            {responseMessage}
                        </div>
                    </div>
                ) : 
                    (!contactNumberValid || !dateValid) && (
                        <div className='message error' role='alert'>
                            <span>
                                { !contactNumberValid && ( <span>Kontaktnummer darf nicht leer sein<br></br></span>  ) }
                                { !dateValid && ( <span>Geburtsdatum darf nicht leer sein<br></br></span>  ) }
                            </span>
                        </div>
                    )
                }
                { currentStep !== 3 && (
                    <Button
                        fullWidth
                        buttonType='primary'
                        onClick={handleSubmit}
                        disabled={
                            isLoading || 
                            (currentStep === 0 && (contactNumber.length <= 0 || !date || formatDate(new Date(date), 'ddmmyyyy').length !== 8)) || 
                            (currentStep === 1 && !passwordCheckPassed) ||
                            (currentStep === 2 && email.length <= 0)
                        }
                    >{currentStep === 2 ? 'Absenden' : 'Weiter'}</Button>
                )}
                <div className="float-center">
                    { (currentStep !== 0 && currentStep !== 3) && (
                        <span
                            className="text-xs mr back"
                            onClick={() => {
                                    setCurrentStep(currentStep - 1);
                                    setResponseMessage('');
                                    setResponseMessageClass('');
                                }
                            }
                        >zurück</span>
                    ) }
                    <Link className="text-xs" to={`/`}>Login</Link>
                </div>
            </form>
        </Card>
    )
}